import { defineStore } from 'pinia';
import { onRequestDefaultError, requests, removeNulls } from '🔗/utils/requests';
import stores from '🔗/stores';
import moment from 'moment';

const useTracksApi = defineStore('tracks-api', {
    state: () => ({
        defaultCover: {
            resized: new URL(`🔗/assets/img/tracks/resized.jpg`, import.meta.url).href,
            original: new URL(`🔗/assets/img/tracks/original.jpg`, import.meta.url).href
        }
    }),
    getters: {
        canView: (state) => (payload) => {
            if (stores.user().isTrackCurator) {
                return true;
            }

            if (payload.track?.state === 'PUBLISHED') {
                return true;
            }

            return !!payload.track?.contributors?.find(item => 
                    (item.artistId || item.artist?.id) &&
                    stores.user().artistsIds.indexOf((item.artistId || item.artist?.id)) !== -1 &&
                    item.role === 'PRIMARY' &&
                    item.state === 'CONFIRMED'
                );
        },

        canListen: (state) => (payload) => {
            if (!state.canView(payload)) {
                return;
            }

            if (stores.user().isTrackCurator) {
                return true;
            }

            const releaseDate = payload?.track?.releaseDate;

            if (!releaseDate || moment().diff(moment(releaseDate)) >= 0) {
                return true;
            }
        },

        canAdd: (state) => (payload) => stores.user().isTrackCurator ||
            stores.user().artistsIds.indexOf(payload?.artist?.id) !== -1,

        canEditLicense: (state) => (payload) => {
            if (stores.user().isTrackCurator) {
                return true;
            }

            return !!payload.track?.contributors?.find(item => 
                    (item.artistId || item.artist?.id) &&
                    stores.user().artistsIds.indexOf((item.artistId || item.artist?.id)) !== -1 &&
                    item.role === 'PRIMARY' &&
                    item.state === 'CONFIRMED'
                );
        },

        canEditCuratorSettings: (state) => (payload) => stores.user().isTrackCurator,
        canEditOwnerSettings: (state) => (payload) => {
            if (stores.user().isTrackCurator) {
                return true;
            }

            return !!payload.track?.contributors?.find(item => 
                    (item.artistId || item.artist?.id) &&
                    stores.user().artistsIds.indexOf((item.artistId || item.artist?.id)) !== -1 &&
                    item.role === 'PRIMARY' &&
                    item.state === 'CONFIRMED'
                );
        },

        canEdit: (state) => (payload) => {
            if (stores.user().isTrackCurator) {
                return true;
            }

            return payload.track?.state !== 'PUBLISHED' &&
                !!payload.track?.contributors?.find(item => 
                    (item.artistId || item.artist?.id) &&
                    stores.user().artistsIds.indexOf((item.artistId || item.artist?.id)) !== -1 &&
                    item.role === 'PRIMARY' &&
                    item.state === 'CONFIRMED'
                );
        },

        canRemove: (state) => (payload) => {
            if (stores.user().isTrackCurator) {
                return true;
            }

            return !!payload.track?.contributors?.find(item => 
                    (item.artistId || item.artist?.id) &&
                    stores.user().artistsIds.indexOf((item.artistId || item.artist?.id)) !== -1 &&
                    item.role === 'PRIMARY' &&
                    item.state === 'CONFIRMED'
                );
        },

        canEditRating: (state) => (payload) => stores.user().isTrackCurator,

        canEditState: (state) => (payload) => {
            if (stores.user().isTrackCurator) {
                return true;
            }

            return !!payload.track?.contributors?.find(item => 
                    (item.artistId || item.artist?.id) &&
                    stores.user().artistsIds.indexOf((item.artistId || item.artist?.id)) !== -1 &&
                    item.role === 'PRIMARY' &&
                    item.state === 'CONFIRMED'
                );
        },

        canEditContributor: (state) => (payload) => {
            if (stores.user().isTrackCurator) {
                return true;
            }

            return !!payload.track?.contributors?.find(item => 
                    (item.artistId || item.artist?.id) &&
                    stores.user().artistsIds.indexOf((item.artistId || item.artist?.id)) !== -1 &&
                    item.role === 'PRIMARY' &&
                    item.state === 'CONFIRMED'
                );
        },

        canEditContributorState: (state) => (payload) => stores.user().isTrackCurator ||
            stores.user().artistsIds.indexOf(payload?.artist?.id) !== -1,

        contributors: (state) => ({
            add: state.addContributor,
            remove: state.removeContributor,
            list: state.listContributor,
            update: state.updateContributor,
            changeState: state.changeContributorState
        })
    },
    actions: {
        async list(params = {}) {
            try {
                const result = await this.$http({
                    method: 'GET',
                    url: '/track/list',
                    params,
                    signal: requests.signal('track-list')
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async get(id) {
            try {
                const result = await this.$http({
                    method: 'GET',
                    url: `/track/${ id }`,
                    signal: requests.signal('track-get', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async stream(id, format, range) {
            try {
                const result = await this.$http({
                    method: 'GET',
                    url: `/track/stream/${id}/${format}`,
                    range,
                    responseType: 'arraybuffer',
                    worker: true
                    // signal: requests.signal('track-get', id)
                });

                if ([200, 206].indexOf(result.status) !== -1 ) {
                    const { data } = result;


                    const match = result.headers['content-range']?.match(/^bytes (\d+)-(\d+)\/(\d+)$/);
                    const range = {};
                    if (match) {
                        range.start = parseInt(match[1], 10);
                        range.end = parseInt(match[2], 10);
                        range.total = parseInt(match[3], 10);
                    }

                    return {
                        data,
                        range
                    }
                }
            } catch (e) {
                if (result.status === 416) {
                    return new Float32Array(0).buffer;
                }
            }
        },
        async downloadWholeFile(id, format, chunkSize = 1024 * 1024 * 2) { // 2MB
            let start = 0;
            let total = Infinity;

            const buffers = [];

            while (start < total) {
                const end = start + chunkSize - 1;

                const chunk = await this.stream(id, format, `bytes=${start}-${end}`);

                if (!chunk || !chunk.data) break;

                buffers.push(chunk.data);

                if (chunk.range?.total) {
                    total = chunk.range.total;
                }

                start = chunk.range.end + 1;
            }

            // Склеиваем все чанки в единый ArrayBuffer
            let totalLength = buffers.reduce((acc, buf) => acc + buf.byteLength, 0);
            let merged = new Uint8Array(totalLength);

            let offset = 0;
            for (let buf of buffers) {
                merged.set(new Uint8Array(buf), offset);
                offset += buf.byteLength;
            }

            return merged.buffer; // полный файл
        },
        async updateCover(id, file) {
            const formData = new FormData();
            formData.append('cover', file);

            try {
                const result = await this.$http({
                    method: 'POST',
                    url: `/track/${id}/upload-cover/`,
                    formData,
                    contentType: 'multipart/form-data',
                    signal: requests.signal('track-cover', id)
                });

                if ([200, 201].indexOf(result.status) !== -1) {
                    return result.data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async updateAudioFile(id, file) {
            const formData = new FormData();
            formData.append('trackFile', file);

            try {
                const result = await this.$http({
                    method: 'POST',
                    url: `/track/${id}/upload-track-file/`,
                    formData,
                    contentType: 'multipart/form-data',
                    signal: requests.signal('track-audio-file', id)
                });

                if ([200, 201].indexOf(result.status) !== -1) {
                    return result.data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async add(data) {
            const formData = new FormData();

            for (const key in data) {
                formData.append(key, data[key]);
            }

            try {
                const result = await this.$http({
                    method: 'POST',
                    url: '/track/create',
                    formData,
                    contentType: 'multipart/form-data',
                    // signal: requests.signal('track-create')
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async update(id, data) {
            try {
                const result = await this.$http({
                    method: 'PUT',
                    url: `/track/update/${ id }`,
                    data,
                    signal: requests.signal('track-update', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                } else {
                    onRequestDefaultError({ response: result });
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async getAvailableStates(id) {
            try {
                const result = await this.$http({
                    method: 'GET',
                    url: `/track/workflow/available/${ id }`,
                    signal: requests.signal('track-workflow-available-states', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async changeState(id, newState) {
            try {
                const result = await this.$http({
                    method: 'POST',
                    url: `/track/workflow/${ id }/${ newState }`,
                    signal: requests.signal('track-change-state', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                } else {
                    onRequestDefaultError({ response: result });
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async remove(id) {
            try {
                const result = await this.$http({
                    method: 'DELETE',
                    url: `/track/${ id }`,
                    signal: requests.signal('track-remove', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },

        // Contributors
        async addContributor(data) {
            try {
                const result = await this.$http({
                    method: 'POST',
                    url: '/track/contributor/create',
                    data: removeNulls(data),
                    // signal: requests.signal('track-contributor-create')
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async updateContributor(id, data) {
            try {
                const result = await this.$http({
                    method: 'PUT',
                    url: `/track/contributor/${ id }`,
                    data,
                    signal: requests.signal('track-contributor-update', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                } else {
                    onRequestDefaultError({ response: result });
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async removeContributor(id) {
            try {
                const result = await this.$http({
                    method: 'DELETE',
                    url: `/track/contributor/${ id }`,
                    signal: requests.signal('track-contributor-remove', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async changeContributorState(id, newState) {
            try {
                const result = await this.$http({
                    method: 'POST',
                    url: `/track/contributor/workflow/${ id }/${ newState }`,
                    signal: requests.signal('track-contributor-change-state', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                } else {
                    onRequestDefaultError({ response: result });
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async listContributor(params = {}) {
            try {
                const result = await this.$http({
                    method: 'GET',
                    url: '/track/contributor/list',
                    params,
                    // signal: requests.signal('track-contributor-list')
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        }
    }
});

export default useTracksApi;
